import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import BoonLogo from '../../../assets/images/boon-logo.svg';
import BoonViewIcon from '../../../assets/images/BoonViewIcon.svg';
import { ReactComponent as SuccessIcon } from '../../../assets/images/referral-success.svg';
import NavigationBar from '../../../components/NavigationBar/NavigationBar';
import { OpeningSelect } from '../../../components/OpeningSelect/OpeningSelect';
import { ReferralProgressBar } from '../../../components/ReferralProgressBar/ReferralProgressBar';
import ButtonWithIcon from '../../../components/shared/ButtonWithIcon/ButtonWithIcon';
import Header from '../../../components/shared/Header/Header';
import { YourInfoSection } from '../../../components/shared/YourInfoSection/YourInfoSection';
import { IsMobile } from '../../../helpers/IsMobile';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { getCompanyProfile } from '../../../redux/actions/companyProfileActions';
import { setCurrentOrganization } from '../../../redux/actions/organizationActions';
import { useGetCandidateQuery } from '../../../redux/apis/applicationApi';
import { useGetOrganizationStrengthenQuestionsQuery } from '../../../redux/apis/organizationApi';
import urls from '../../../routing/urls';
import { selectCompanyProfile } from '../../../selectors/companyProfileSelectors';
import { selectCurrentOrganization } from '../../../selectors/organizationSelectors';

import styles from './ApplicationSuccess.module.scss';

const calculateApplicationStrength = (candidate, applicationStrengthenQuestions = {}) => {
  if (!candidate) return 17;

  let answered = 0;
  let total = 0;

  const check = (enabled, hasValue) => {
    if (enabled) {
      total += 1;
      if (hasValue) answered += 1;
    }
  };

  check(applicationStrengthenQuestions.current_title, !!candidate.questions?.current_title);
  check(applicationStrengthenQuestions.current_employer, !!candidate.questions?.current_employer);
  check(
    applicationStrengthenQuestions.current_location,
    !!(candidate.questions?.current_location || candidate?.location?.full_address)
  );
  check(applicationStrengthenQuestions.why_are_they_good_fit, (candidate.skills?.length || 0) > 0);
  check(applicationStrengthenQuestions.joining_date, !!candidate.questions?.joining_date);

  // Diversity hire: only counts as answered when the applicant actively ticked it.
  check(applicationStrengthenQuestions.diversity_hire, candidate?.questions?.diversity_hire === true);

  // Baseline strength: every referral/application starts at 17%. The remaining
  // 83% is earned by answering enabled questions and added on top of the base.
  const BASE_STRENGTH = 17;

  if (total === 0) return 100;
  return Math.min(100, BASE_STRENGTH + Math.round((answered / total) * (100 - BASE_STRENGTH)));
};

export const ApplicationSuccess = () => {
  const { t } = useTranslation('common');
  const { state } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useCurrentUser();
  const { companyName, applicationId } = useParams();
  const companyProfile = useSelector(selectCompanyProfile);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const company = companyProfile || currentOrganization;
  const orgId = company?.name === 'Boon' ? company?.id : company?.id;
  const { data: candidateData } = useGetCandidateQuery({ organizationId: company?.id, applicationId });
  const { data: strengthenQuestionsData } = useGetOrganizationStrengthenQuestionsQuery(
    { organizationId: orgId },
    { skip: !orgId }
  );
  const candidate = candidateData?.payload;
  const applicationStrengthenQuestions = strengthenQuestionsData?.payload || {
    enable_application_questions: true,
    current_title: true,
    current_employer: true,
    current_location: true,
    diversity_hire: true,
    joining_date: true,
    why_are_they_good_fit: true,
  };
  const personalInfo = {
    firstName: candidate?.name?.split(' ')[0],
    lastName: candidate?.name?.split(' ')[1],
    email: candidate?.email,
    avatar_thumb: candidate?.avatar_thumb,
  };
  const isMobile = IsMobile();

  const applicationStrength = calculateApplicationStrength(candidate, applicationStrengthenQuestions);
  // Progress bar is only meaningful when the master toggle is on AND at least one
  // individual question is enabled in Admin Web. Otherwise, hide it entirely.
  const hasAnyApplicationQuestionEnabled =
    !!applicationStrengthenQuestions.enable_application_questions &&
    !!(
      applicationStrengthenQuestions.current_title ||
      applicationStrengthenQuestions.current_employer ||
      applicationStrengthenQuestions.current_location ||
      applicationStrengthenQuestions.why_are_they_good_fit ||
      applicationStrengthenQuestions.joining_date ||
      applicationStrengthenQuestions.diversity_hire
    );

  const handleSendAnotherReferral = () => {
    const url = companyName ? urls.companyApplicationFlow : urls.applicationFlow;
    navigate(url.replace(':companyName', companyName));
  };

  const handleViewInTracker = () => {
    navigate(`${urls.tracker}?application=true`);
  };

  useEffect(() => {
    if (companyName) {
      dispatch(getCompanyProfile(companyName, navigate));
    }
  }, [dispatch, companyName, navigate]);

  useEffect(() => {
    if (company) {
      dispatch(setCurrentOrganization(company));
    }
  }, [company]);

  return (
    <div className="flex w-full">
      {isAuthenticated && !companyName && <NavigationBar />}
      <div className="w-full h-[100vh]">
        <Header
          isPublic={!isAuthenticated || companyName}
          title={t('application.apply')}
          backButton={isAuthenticated && !companyName ? t('back') : ''}
          logo={isMobile ? BoonViewIcon : BoonLogo}
          logoClassName={isMobile ? 'w-full h-full' : 'w-full h-[20px] rounded-none'}
        />
        <div className={styles.outerContainer}>
          <div className={styles.container}>
            <div className={styles.formSection}>
              <div className={styles.formHeader}>
                <h2>{t('application.applyTo', { company: company?.name })}</h2>
              </div>
              <div className={styles.successContainer}>
                <SuccessIcon />
                <div className={styles.successTextContainer}>
                  <p className={styles.successTextTitle}>{t('success')}</p>
                  <p className={styles.successTextDescription}>{t('application.completedSuccessfully')}</p>
                </div>
              </div>

              <p className={styles.referralSummaryTitle}>{t('application.applicationSummary')}</p>
              <div className={styles.previousInfo}>
                <YourInfoSection personalInfo={personalInfo} />
                <div className="space-y-[6px]">
                  <label htmlFor="opening" className={styles.formLabel}>
                    {t('application.openingInfo')}
                  </label>
                  <OpeningSelect
                    organizationId={company?.name === 'Boon' ? 'Boon' : company?.id}
                    value={candidate?.job_id}
                    isDisabled
                  />
                </div>
              </div>

              <div className={styles.nextButtonContainer}>
                <ButtonWithIcon
                  type="button"
                  variant="tertiary-36"
                  text={t('buttons.sendAnotherApplication')}
                  textClassName="text-[#314155]"
                  onClick={handleSendAnotherReferral}
                />
                <ButtonWithIcon
                  variant="secondary-36"
                  text={t('buttons.viewInTracker')}
                  textClassName="text-white"
                  onClick={handleViewInTracker}
                />
              </div>
            </div>
          </div>
          {hasAnyApplicationQuestionEnabled && (
            <div className={styles.applicationStrengthContainer}>
              <ReferralProgressBar
                title={t('application.applicationStrength')}
                applicationStrength={applicationStrength}
              />
              <div className={styles.applicationStrengthDescriptionContainer}>
                <p className={styles.applicationStrengthDescription}>
                  {applicationStrength === 100 ? t('application.greatJob') : t('application.strengthNotStrong')}
                </p>
              </div>
              {applicationStrength !== 100 && (
                <ButtonWithIcon
                  type="button"
                  variant="tertiary-36"
                  text={t('buttons.makeIt100')}
                  containerClass="self-end"
                  textClassName="text-[#314155]"
                  onClick={() => {
                    const url = companyName ? urls.companyApplicationFlowStrengthen : urls.applicationFlowStrengthen;
                    navigate(url.replace(':companyName', companyName).replace(':applicationId', applicationId), {
                      state,
                    });
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
